/**
 * @file Precomputed values and Padé approximant coefficients for inverse
 * trigonometric functions.
 *
 * @internal
 */

import type { TwoF64 } from '../base/common.js';

/**
 * Coefficients (from lowest to highest degree) of the Padé (n,n) approximant
 * for asin(x). Trivial zeros are skipped, which means :
 * - for the numerator:   index `k` maps to degree `2k + 1`
 * - for the denominator: index `k` maps to degree `2k`
 *
 * @internal
 */
export const asin_pade = {
  25: [
    [
      [1.0, 0.0],
      [-5.773536659956517, -2.499001563577705e-16],
      [14.564635646270721, -7.116774171660272e-16],
      [-21.078346953234156, 7.380601859726813e-17],
      [19.319155238103882, -1.1413678417611188e-15],
      [-11.680108928527208, 3.6859752371370374e-16],
      [4.7052825858577725, 1.7493679968510534e-16],
      [-1.2467463242936092, 5.0750797590537705e-17],
      [0.20940060214415043, -1.0815621971858014e-17],
      [-0.020783557311067288, -1.0614802262890162e-18],
      [0.0010717177916740347, 8.910822337987105e-20],
      [-2.204530295145231e-5, -4.776085312255094e-23],
      [8.323829936524401e-8, -4.419445725217229e-24],
    ],
    [
      [1.0, 0.0],
      [-5.940203326623184, 4.615931687560462e-17],
      [15.479669534041252, -2.456754794718945e-16],
      [-23.257419483220485, 1.4477870336732925e-15],
      [22.269222307653052, 6.814439863288127e-16],
      [-14.18029342582076, -3.023267208130291e-16],
      [6.081990755552385, 1.1316731919170236e-16],
      [-1.7416450510733874, 6.077434959822141e-17],
      [0.32309784969193583, 4.6165190919204216e-18],
      [-0.03664517014959065, -1.7847548807424954e-18],
      [0.0022912035782555176, -1.3123623148596213e-19],
      [-6.479182269081067e-5, -4.5014961111357546e-21],
      [5.152003324241701e-7, -5.1380499855243576e-23],
    ],
  ] as [TwoF64[], TwoF64[]],
  26: [
    [
      [1.0, 0.0],
      [-6.021825891614485, -8.306267409224574e-17],
      [15.935623779818842, -3.9924821386094816e-16],
      [-24.364888560407174, -9.115523649818412e-16],
      [23.803031854876792, 1.4399267686959294e-15],
      [-15.515156672756861, 7.217516791341468e-16],
      [6.840496039603421, 2.4292420920129953e-16],
      [-2.024860769751863, 1.253622615342802e-16],
      [0.3912935000869534, -1.0284012134023445e-17],
      [-0.046748972050096815, -3.9291119357232314e-19],
      [0.0031335159149479814, 6.119677601676857e-20],
      [-9.801700898826112e-5, 2.7422122495670493e-21],
      [9.260333212533493e-7, 1.780682280183938e-23],
    ],
    [
      [1.0, 0.0],
      [-6.188492558281152, 2.1299679914112934e-16],
      [16.892039206199033, 1.869772134056179e-16],
      [-26.760734343378783, -1.716813716348112e-15],
      [27.242141349739533, -1.0231553269873048e-15],
      [-18.636921103750947, 9.531421091564578e-16],
      [8.70604877490177, -2.966281645962223e-16],
      [-2.7657124752928506, -1.380487398870196e-16],
      [0.5840694624441274, 3.876289190889445e-17],
      [-0.07836330214385936, 3.086040050322331e-19],
      [0.00616848820701362, 1.7782764591143423e-19],
      [-0.000246722708619138, -1.1069633872067745e-20],
      [3.709565408023384e-6, -3.063154477679588e-24],
      [-7.634948785609296e-9, 8.124919384181399e-25],
    ],
  ] as [TwoF64[], TwoF64[]],
  27: [
    [
      [1.0, 0.0],
      [-6.273467445665173, 8.922623917066904e-17],
      [17.388485517298918, 8.465553199212899e-16],
      [-28.029956108616787, 1.0098356194699985e-15],
      [29.107219839008224, -1.1071902852502964e-15],
      [-20.37665891981981, -6.783981543384106e-16],
      [9.779696801859325, -6.467902274145351e-16],
      [-3.2089053919047528, -5.39276428588791e-17],
      [0.7050266826391803, -4.121704504082678e-17],
      [-0.09944286499280067, -8.998146312222082e-19],
      [0.008363522575406493, 1.3169223509594426e-19],
      [-0.0003677057395888868, -1.3939732902608136e-21],
      [6.473547615475028e-6, -7.776224129697325e-23],
      [-2.0946215008806403e-8, -1.7043518378971078e-25],
    ],
    [
      [1.0, 0.0],
      [-6.44013411233184, 3.852857124040441e-16],
      [18.38684120268756, -1.1124395941729845e-15],
      [-30.656062441116013, -1.4643541006623212e-15],
      [33.094674531705884, 8.469364260246236e-16],
      [-24.24078281274901, 1.1671781517574094e-15],
      [12.274399849470061, 2.013095594492405e-16],
      [-4.296194080741971, -1.858456640574996e-16],
      [1.0223397297009247, -1.0918765831019797e-16],
      [-0.1596681471507737, -1.034045696579487e-17],
      [0.015389935771908498, -7.556314630662735e-19],
      [-0.0008232847376978536, -2.6750302809247004e-20],
      [2.0013880359715614e-5, -2.2396832062005644e-22],
      [-1.3728076108425853e-7, 3.587106844558443e-24],
    ],
  ] as [TwoF64[], TwoF64[]],
  28: [
    [
      [1.0, 0.0],
      [-6.521899946350561, -2.26039599875206e-16],
      [18.88450278224472, 6.790296592172175e-16],
      [-31.988882974218715, 8.409942130711366e-16],
      [35.16025003299098, -2.665566231632599e-15],
      [-26.2901056474208, -1.7710978088846401e-15],
      [13.634165972597893, 5.481086057620196e-16],
      [-4.908306928951923, 6.641357774978804e-17],
      [1.208067907208375, -5.595118397754601e-17],
      [-0.19665288772134054, -4.51280233904373e-18],
      [0.01997748660628335, 1.0728353851712141e-18],
      [-0.001146172934276875, -4.455640782830379e-20],
      [3.082415826813088e-5, -1.1701521624618316e-21],
      [-2.510029887257711e-7, -1.6581838929813394e-23],
    ],
    [
      [1.0, 0.0],
      [-6.688566613017228, 7.001987335816909e-17],
      [19.92426388441426, -3.3924252866928593e-16],
      [-34.85259398278765, -2.7598219293718364e-15],
      [39.74291068480882, -1.4054175868786114e-15],
      [-31.00861611228961, -1.080726824181892e-15],
      [16.90441676435119, -1.4036473871827209e-15],
      [-6.459059548979776, 3.9424845416368135e-16],
      [1.7094325961147017, -1.0053571077339934e-16],
      [-0.30476998427973856, 1.3598011341322976e-17],
      [0.034857926885472096, 1.3214024865405004e-18],
      [-0.002355737688414572, -1.1350786158675781e-19],
      [8.130781983784037e-5, 1.3408202138475279e-21],
      [-1.0588573718555191e-6, 6.180194031291226e-23],
      [1.8967694194137826e-9, 1.0080787257627721e-25],
    ],
  ] as [TwoF64[], TwoF64[]],
  29: [
    [
      [1.0, 0.0],
      [-6.773408534292065, 1.6563116718920444e-16],
      [20.46233372100096, -1.0076561105397027e-15],
      [-36.362236413115845, -2.0431927172567507e-15],
      [42.2100247139315, -7.836031631681018e-16],
      [-33.61082140803267, -1.0547805919393302e-15],
      [18.759070684487416, -1.5973883151098647e-15],
      [-7.3679846620835985, 3.06783462535185e-16],
      [2.015135171164216, -1.1046724221014779e-16],
      [-0.3739731531330032, 6.533781033843508e-18],
      [0.044988221161480206, -1.4762686796279129e-18],
      [-0.003249670687434248, 1.161511804965686e-19],
      [0.00012330530961011797, -1.1479434594933279e-20],
      [-1.8791558084659818e-6, -8.993126490016285e-24],
      [5.2681529595840235e-9, -2.450438214613586e-25],
    ],
    [
      [1.0, 0.0],
      [-6.940075200958731, -4.264877792775457e-16],
      [21.544012921160746, 7.213582361134553e-16],
      [-39.47704245038026, 3.1130495907804193e-15],
      [47.45317366122046, 3.051222242594384e-15],
      [-39.33221097214259, -1.1301391622884664e-15],
      [23.001181820182357, -3.494416466391499e-16],
      [-9.546176522949393, 5.515165203681005e-16],
      [2.78996230288476, -6.159137966255521e-17],
      [-0.5618972060933163, -4.501625786542309e-17],
      [0.07501583711459937, -2.309605599872083e-18],
      [-0.006226438702917709, 2.70646163102396e-19],
      [0.00028837100169001856, -2.5324945604433575e-20],
      [-6.092520160357781e-6, -1.3827042525267844e-22],
      [3.642405979125053e-8, 2.445537784081482e-25],
    ],
  ] as [TwoF64[], TwoF64[]],
  30: [
    [
      [1.0, 0.0],
      [-7.021963257933044, 3.3006986403582366e-16],
      [22.08338275978024, 1.3056287858600981e-15],
      [-41.05606878325399, 1.9359756041962193e-15],
      [50.16101268117207, -2.997061430421424e-15],
      [-42.35006763813048, 3.5273558357543953e-15],
      [25.293600628174243, -1.2381775513967212e-15],
      [-10.756821017477913, 6.346893244568494e-16],
      [3.2351180434349653, -9.862642001845251e-17],
      [-0.6742441379487172, 1.1464047342469986e-17],
      [0.09386722532225855, -4.470168598119907e-18],
      [-0.008215169349900372, -5.174834936731037e-19],
      [0.00040819200653407703, 1.0625936911328878e-20],
      [-9.540645948267062e-6, 5.75666284016149e-24],
      [6.765613145537793e-8, -6.386872935687632e-25],
    ],
    [
      [1.0, 0.0],
      [-7.18862992459971, -2.620490824309265e-16],
      [23.206487747213526, -3.974472591449938e-16],
      [-44.42931235392079, 3.543404716603276e-15],
      [56.11595052678351, 2.768580128838814e-15],
      [-49.21049915409337, -3.420749387632164e-15],
      [30.70851982846172, -1.3547747265067824e-15],
      [-13.747849160124641, 3.774553095762628e-16],
      [4.395392064062057, -2.8684653466542713e-16],
      [-0.9867847526034879, -3.510803359882401e-18],
      [0.1507678563313154, 1.209876883932213e-17],
      [-0.01489054108663369, -4.563383406804438e-19],
      [0.000873900731804542, 1.7792708046244046e-20],
      [-2.6301962850002732e-5, -1.3579110210331937e-21],
      [2.996080612280128e-7, 2.0388225164245315e-23],
      [-4.714509988530537e-10, 2.3940608643681237e-26],
    ],
  ] as [TwoF64[], TwoF64[]],
};

/**
 * Coefficients (from lowest to highest degree) of the Padé (n,n) approximant
 * for atan(x). Trivial zeros are skipped, which means :
 * - for the numerator:   index `k` maps to degree `2k + 1`
 * - for the denominator: index `k` maps to degree `2k`
 *
 * @internal
 */
export const atan_pade = {
  25: [
    [
      [1.0, 0.0],
      [5.789115646258503, 1.2084060131974493e-17],
      [14.637689969604864, -6.824665790279381e-16],
      [21.222029237226806, -6.150015284187869e-16],
      [19.47196772726668, 8.37151659952202e-17],
      [11.77379550664489, -6.063759622792698e-16],
      [4.736992629152821, 4.2973493007670867e-16],
      [1.2510108684438501, -1.0947368296595395e-16],
      [0.2087697835566165, 1.2772267654413591e-17],
      [0.02048135870057718, 1.9863662226145724e-19],
      [0.0010337942093040133, 6.579182829110248e-20],
      [2.0339481776714117e-5, 6.124761003474323e-23],
      [6.587407380753314e-8, -2.580240539415183e-24],
    ],
    [
      [1.0, 0.0],
      [6.122448979591836, 3.0814353336534957e-16],
      [16.478506296135475, 7.821215089673703e-16],
      [25.63323201621074, -1.1518423274733134e-15],
      [25.48420159751184, -1.4756305631322361e-15],
      [16.90659227932493, -1.2735701908565494e-15],
      [7.586291407389391, -2.4902468389400212e-17],
      [2.2846746323412064, 1.039513749261809e-16],
      [0.44877537420987984, 4.558691151569013e-18],
      [0.054397015055743014, -2.8117435714012e-18],
      [0.003684959084421301, -1.3731207089563076e-19],
      [0.00011551595875928843, -7.7824750784513085e-22],
      [1.069592210734152e-6, 3.985139051556177e-23],
    ],
  ] as [TwoF64[], TwoF64[]],
  26: [
    [
      [1.0, 0.0],
      [6.03921568627451, 1.3932210505100003e-16],
      [16.02299586501267, 5.892282497292975e-16],
      [24.552084663652696, -1.029791807742357e-15],
      [24.025556321819508, 1.0159281592805589e-16],
      [15.67470384669387, 8.299104714314099e-17],
      [6.910297027612256, -3.517924072291741e-16],
      [2.042450538159876, -4.378422999466977e-17],
      [0.3932756215602616, 9.996336031343255e-18],
      [0.04666585807700284, 7.124280497490563e-19],
      [0.0030899399522942117, -8.518259336265183e-20],
      [9.451369187692818e-5, 3.0526231664507285e-21],
      [8.521819937874166e-7, 3.424895987555767e-24],
    ],
    [
      [1.0, 0.0],
      [6.372549019607843, 4.353815782843751e-16],
      [17.94717887154862, 3.198721799640307e-16],
      [29.40282495977114, 9.775838282985823e-16],
      [31.036315235313985, -1.533954727040747e-15],
      [22.08630805117693, -3.31489507034471e-16],
      [10.77380880545216, -8.115888471144678e-16],
      [3.5912696018173866, 2.55298575285525e-17],
      [0.8007560598646876, 2.6918654634606075e-18],
      [0.1143937228378125, 6.332175555271997e-18],
      [0.00970613405896591, -3.2482949852297105e-19],
      [0.00042695604364952685, -1.8422009683506517e-20],
      [7.361311097405635e-6, 1.4970766601088096e-22],
      [2.0972396288904943e-8, -5.161384017388323e-25],
    ],
  ] as [TwoF64[], TwoF64[]],
  27: [
    [
      [1.0, 0.0],
      [6.289308176100629, -3.798498901862171e-16],
      [17.470810210876802, 1.1363952078027796e-15],
      [28.212860993453987, -1.3121921296516906e-15],
      [29.333026452920254, 1.0268746868433764e-15],
      [20.54449673741983, 1.3657126837917721e-15],
      [9.85483486057265, 7.783299933713562e-17],
      [3.2271368190726792, -5.854618315656083e-17],
      [0.7061440444489196, -4.833126833065838e-17],
      [0.09887761304713705, -2.701060656974443e-18],
      [0.008212173944743406, 3.3111009709258166e-19],
      [0.00035305785964959304, -1.2792772325450851e-20],
      [5.938933633098827e-6, 2.9728719542305433e-22],
      [1.6474611133515503e-8, -1.2561210163893847e-24],
    ],
    [
      [1.0, 0.0],
      [6.622641509433962, -8.379041695284201e-17],
      [19.478357380688124, 6.939818062917738e-16],
      [33.52397562798704, 1.8711114285594066e-15],
      [37.44699405253872, -9.335148330351317e-16],
      [28.459715479929425, 1.4221569341736005e-15],
      [15.002020563063574, -6.273566853970559e-16],
      [5.4885441084378925, 2.903885802889553e-16],
      [1.3721360271094731, 7.259714507223882e-17],
      [0.2266290735466097, 2.381586101184943e-19],
      [0.023310418993365566, 1.6700054399673307e-18],
      [0.0013485366359798263, -7.005059775586007e-20],
      [3.625098483816737e-5, 1.760071876473871e-21],
      [2.8846937537003216e-7, -2.432966587030474e-23],
    ],
  ] as [TwoF64[], TwoF64[]],
  28: [
    [
      [1.0, 0.0],
      [6.539393939393939, 3.014423727467092e-16],
      [18.9811320754717, -6.703233356227361e-16],
      [32.219984144601234, 2.4439425519884264e-15],
      [35.47315328647182, 1.0106548173951159e-15],
      [26.552946328708483, 1.1961164485270971e-15],
      [13.774855136469677, -8.788654215996232e-16],
      [4.955303725806584, 3.2163545560816454e-16],
      [1.2169318462734648, 3.1860040803710645e-17],
      [0.19723024704968617, 4.644326510294447e-20],
      [0.019882640681667958, 9.776237930997203e-19],
      [0.0011258078511495863, -4.3561573646592965e-20],
      [2.957546240780917e-5, -1.2906470484325948e-21],
      [2.2959319551465674e-7, -6.186713621742159e-25],
    ],
    [
      [1.0, 0.0],
      [6.872727272727273, -2.90676573720041e-16],
      [21.07204116638079, 2.9859857677740057e-16],
      [38.012309555039856, -1.2725389526527877e-15],
      [44.80022197558269, -1.246012788569321e-15],
      [36.22145606536473, -2.0656697333796004e-15],
      [20.525491770373343, 1.4347771822052606e-15],
      [8.182920307125586, -8.266555118711897e-16],
      [2.2702614266720373, 1.4433832420225312e-16],
      [0.4268867639896139, -7.020168856418761e-18],
      [0.05191866048522331, -8.538043203752548e-19],
      [0.0037759025807435134, 6.40668476801129e-20],
      [0.00014302661290695128, -6.6082435707082604e-21],
      [2.129428480004237e-6, 1.0338883749916444e-22],
      [5.2448977340005846e-9, -2.0172320126284177e-25],
    ],
  ] as [TwoF64[], TwoF64[]],
  29: [
    [
      [1.0, 0.0],
      [6.7894736842105265, -2.337311630789803e-16],
      [20.55396065922382, -1.53365417713238e-15],
      [36.58907990387633, -3.0106055266490083e-15],
      [42.52861836426063, 2.7268707175256356e-15],
      [33.88854209875298, -2.8477363924817366e-15],
      [18.91261763794883, -4.2117103685646513e-16],
      [7.419798312948198, -1.490272539933992e-16],
      [2.0239733984753725, 2.812628053695184e-17],
      [0.3738225694892246, -2.708688368824741e-18],
      [0.04460992892056922, -1.21622398142925e-18],
      [0.0031795062156404416, -2.0153007009438378e-19],
      [0.00011786808185446975, 3.3133777059524176e-22],
      [1.714800202962657e-6, 1.9934501004417088e-23],
      [4.119966548222059e-9, -3.4816752021327533e-25],
    ],
    [
      [1.0, 0.0],
      [7.12280701754386, 6.232831015439475e-17],
      [22.72822966507177, 1.329292869292819e-15],
      [42.88345219824863, -3.2566809329728524e-15],
      [53.183889245867164, 1.4294697014765961e-15],
      [45.586190782171855, 1.2252597441227967e-15],
      [27.642690155146763, 6.673870006106214e-16],
      [11.934621781269714, 9.076870699693887e-17],
      [3.6428351367247673, -2.9096543600942366e-17],
      [0.7700301915027964, -5.037888580721763e-17],
      [0.10859400136577897, -1.0554225437654154e-18],
      [0.009605366214172096, 1.3254332346736073e-19],
      [0.0004802683107086048, -4.214855551487007e-21],
      [1.1195065517683095e-5, 5.138267089369768e-22],
      [7.738524551393845e-8, -1.8153467238211883e-24],
    ],
  ] as [TwoF64[], TwoF64[]],
  30: [
    [
      [1.0, 0.0],
      [7.03954802259887, -1.9068237259098733e-16],
      [22.1892952720785, 1.616944263090112e-15],
      [41.33577394951168, 5.213187605649773e-16],
      [50.586009582104715, -2.5208231202398453e-15],
      [42.759467437415715, 2.6025496619719528e-15],
      [25.552828123361806, -6.439205533941275e-16],
      [10.864536097596131, -7.055402794715267e-16],
      [3.263167699219749, -1.5833462999263625e-17],
      [0.678145995809767, -1.9550486163847003e-17],
      [0.09393215246168907, 3.914449393993114e-18],
      [0.008151644429521723, 8.022447044030069e-19],
      [0.0003994037353831055, 2.4187465701013508e-20],
      [9.110864397124866e-6, 6.162440629429035e-22],
      [6.153533301501549e-8, 2.963504723950062e-24],
    ],
    [
      [1.0, 0.0],
      [7.372881355932203, 1.0537710064238774e-16],
      [24.44692239072257, -5.387701386227343e-16],
      [48.153028951423245, -2.7837417536753257e-15],
      [62.689792408456675, -1.4790819487166308e-15],
      [56.789576652366634, 8.121123562395828e-17],
      [36.70074681615531, -1.5426124858269185e-15],
      [17.067520555841224, -1.6352594924910673e-15],
      [5.689173518613741, 4.703244896972775e-17],
      [1.3377643157463834, 3.5730932475507935e-17],
      [0.2153474264372227, -5.6212077074198385e-18],
      [0.02258889088502336, 1.8674256539313344e-19],
      [0.0014245246504068787, -7.222469122183247e-20],
      [4.696235111231468e-5, -1.189602817768648e-21],
      [6.099006637962946e-7, -2.5074761644365725e-23],
      [1.3116143307447194e-9, 7.764348207309179e-28],
    ],
  ] as [TwoF64[], TwoF64[]],
};

/**
 * Integer coefficients (from highest to lowest degree) of the Padé (n,n)
 * approximant for `atan(x)`. Trivial zeros are skipped, which means index `k`
 * starting from the last element maps to degree `2k + 1` for the numerator and
 * `2k` for the denominator.
 *
 * @internal
 */
export const atan_pade_int = {
  25: [
    [
      [5.80542139465728e14, 0.0],
      [1.7924997777999098e17, -1.0],
      [9.110733060022169e18, -1.0],
      [1.805003260302135e20, 1109.0],
      [1.8398688557788137e21, -88747.0],
      [1.1025043451589568e22, 161286.0],
      [4.1746679332397375e22, 1.650758e6],
      [1.037613743614859e23, 5.620794e6],
      [1.7160465643924222e23, -1.3117062e7],
      [1.8702778718651183e23, 1.4314603e7],
      [1.2900061223811325e23, -6.427669e6],
      [5.10189424858236e22, 2.270017e6],
      [8.812907808949554e21, -460543.0],
    ],
    [
      [9.426217546370624e15, 1.0],
      [1.0180314950080275e18, -20.0],
      [3.2475204690756076e19, 1730.0],
      [4.79395878768304e20, 572.0],
      [3.955015999838508e21, -44433.0],
      [2.0134626908268768e22, 155096.0],
      [6.685728678514886e22, 407452.0],
      [1.4899623912118888e23, 2.825432e6],
      [2.2458991926355678e23, -1.0174417e7],
      [2.2590331060427934e23, -1.2196164e7],
      [1.4522355681703671e23, -5.443646e6],
      [5.395657842214012e22, 3.514604e6],
      [8.812907808949554e21, -460543.0],
    ],
  ] as [TwoF64[], TwoF64[]],
  26: [
    [
      [2.9463097594804732e16, -1.0],
      [3.2676894702258806e18, 124.0],
      [1.0683070405174632e20, 170.0],
      [1.6134120890742803e21, 94356.0],
      [1.359699935478568e22, 332169.0],
      [7.06151033195586e22, -1.88932e6],
      [2.3891464172900398e23, 5.1023e6],
      [5.419327474314231e23, -5.08476e6],
      [8.306527430053033e23, -3.6246179e7],
      [8.488567839671589e23, -5.5297428e7],
      [5.5397449649679735e23, -1.4159014e7],
      [2.08798123473574e23, 1.360922e7],
      [3.45737152504944e22, 129087.0],
    ],
    [
      [7.25093657413125e14, 0.0],
      [2.5450787375200688e17, -5.0],
      [1.4761456677616398e19, 414.0],
      [3.355771151378128e20, 6954.0],
      [3.955015999838508e21, -44433.0],
      [2.7685111998869555e22, 1.261833e6],
      [1.2416353260099074e23, -441676.0],
      [3.724905978029722e23, 3.2229404e7],
      [7.636057254960931e23, -5.4725677e7],
      [1.0730407253703268e24, -7.600131e6],
      [1.016564897719257e24, -2.1328306e7],
      [6.205006518546114e23, 2.66921e6],
      [2.2032269522373884e23, -7.319271e6],
      [3.45737152504944e22, 129087.0],
    ],
  ] as [TwoF64[], TwoF64[]],
  27: [
    [
      [1.0062730417405952e16, 0.0],
      [3.6275143390280607e18, -191.0],
      [2.156485536810415e20, -8920.0],
      [5.016014756104553e21, 71378.0],
      [6.039467374047839e22, 1.526776e6],
      [4.313144084287662e23, 2.5441159e7],
      [1.9711426004070898e24, -9.7039536e7],
      [6.019355826144826e24, 3.098038e7],
      [1.254862591623534e25, 5.55222064e8],
      [1.7916680104326564e25, -9.29100289e8],
      [1.723248046221377e25, -7.9752908e8],
      [1.0671211107864362e25, 4.4910821e8],
      [3.8415239167216e24, 1.8211516e8],
      [6.108023027587344e23, 3.1640665e7],
    ],
    [
      [1.7619775875138938e17, -1.0],
      [2.21421850164246e19, -1427.0],
      [8.23689282610995e20, 46858.0],
      [1.423805759941863e22, -369674.0],
      [1.3842555999434778e23, -2.079443e6],
      [8.381038450566875e23, 5.407295e6],
      [3.35241538022675e24, 2.162918e7],
      [9.163268705953116e24, 4.170337e8],
      [1.7383259750999293e25, 6.01653609e8],
      [2.2872710198683283e25, -1.016757797e9],
      [2.0476521511202177e25, -5.8300471e8],
      [1.1897425542081898e25, -8.65002682e8],
      [4.045124684307845e24, -1.65251893e8],
      [6.108023027587344e23, 3.1640665e7],
    ],
  ] as [TwoF64[], TwoF64[]],
  28: [
    [
      [1.101854698356411e18, -9.0],
      [1.4193740427306176e20, 5437.0],
      [5.402933076718806e21, -103974.0],
      [9.541999274725721e22, 5.334294e6],
      [9.465396998484617e23, -3.0399659e7],
      [5.84025179574784e24, 6.8397935e7],
      [2.3781300137505415e25, -2.9285924e7],
      [6.610774686625732e25, 3.355237796e9],
      [1.2743186313474748e26, 4.254636529e9],
      [1.7024137203456867e26, -1.2824389109e10],
      [1.5462888972435415e26, 6.290060586e9],
      [9.109350784808834e25, 5.549439078e9],
      [3.138360403222259e25, 1.446770531e9],
      [4.7991609502471987e24, 2.48605225e8],
    ],
    [
      [2.5171108393055624e16, 1.0],
      [1.0219470007580584e19, -186.0],
      [6.864077355091625e20, 17203.0],
      [1.8121164217441891e22, -279844.0],
      [2.49166007989826e23, -1.3809327e7],
      [2.048698287916347e24, 4.3043994e7],
      [1.0895349985736938e25, -7.35011533e8],
      [3.927115159694192e25, 4.241554312e9],
      [9.8505138588996e25, 7.704337747e9],
      [1.7383259750999294e26, -2.573398502e9],
      [2.1500347586762286e26, -1.0416516751e10],
      [1.8242719164525577e26, -1.6126685988e10],
      [1.0112811710769612e26, 3.384895443e9],
      [3.298332434897166e25, -1.512629562e9],
      [4.7991609502471987e24, 2.48605225e8],
    ],
  ] as [TwoF64[], TwoF64[]],
  29: [
    [
      [1.1270258067494666e18, 0.0],
      [4.690873237774582e20, 24815.0],
      [3.2243070055841165e22, 1.375253e6],
      [8.697608380566818e23, 4.8205162e7],
      [1.2203143045527241e25, -3.30252698e8],
      [1.0225997663538413e26, 2.861459709e9],
      [5.536623236032209e26, 2.9537476279e10],
      [2.0297019603660292e27, -1.5379364292e10],
      [5.173587673995025e27, 4.325322372e9],
      [9.270284370365182e27, -3.94120894919e11],
      [1.1633796017738356e28, -1.67478407181e11],
      [1.0009022358368995e28, 3.5729396537e11],
      [5.622580626013855e27, -3.265226209e10],
      [1.857275287745666e27, -2.5659474949e10],
      [2.7355217416409034e26, 7.48725025e8],
    ],
    [
      [2.116890215855978e19, -239.0],
      [3.0624345122716485e21, -225722.0],
      [1.313784405764537e23, 5.835451e6],
      [2.6275688115290743e24, -8.4617572e7],
      [2.970612517478703e25, 1.832097465e9],
      [2.106434330575808e26, -1.2778567206e10],
      [9.965054717724014e26, -1.1555901797e10],
      [3.2647417360924387e27, 1.76807220488e11],
      [7.561717991684693e27, -3.86820741781e11],
      [1.2470201600322126e28, -3.29280064614e11],
      [1.4548568533709146e28, -3.84160075383e11],
      [1.173086158449275e28, 2.55208917916e11],
      [6.217356639781157e27, 1.57250959051e11],
      [1.9484593458003627e27, 5.4762822918e10],
      [2.7355217416409034e26, 7.48725025e8],
    ],
  ] as [TwoF64[], TwoF64[]],
  30: [
    [
      [6.6210288261064016e19, -3807.0],
      [9.803033939767665e21, -294426.0],
      [4.29747190054343e23, -2.7543313e7],
      [8.770940223052504e24, -3.52414708e8],
      [1.0106835515057756e26, 2.338464081e9],
      [7.296660254474398e26, 9.792792746e9],
      [3.511076700548806e27, 7.6743063687e10],
      [1.1689935385074542e28, -7.25069340952e11],
      [2.7494124644135274e28, 6.87343601547e11],
      [4.600798478217453e28, -3.265662015398e12],
      [5.442912408698299e28, 1.992580825429e12],
      [4.447613061626754e28, -3.634461773748e12],
      [2.3875057861727774e28, 8.01368907907e11],
      [7.574355755743479e27, 1.19992830422e11],
      [1.075971885045422e27, 1.4398231221e10],
    ],
    [
      [1.4112601439039854e18, -33.0],
      [6.562359669153532e20, -48369.0],
      [5.05301694524822e22, -1.627261e6],
      [1.53274847339196e24, -9.4099493e7],
      [2.4305011506643935e25, 1.498988835e9],
      [2.3170777636333885e26, 9.995392931e9],
      [1.4393967925601353e27, -1.420863321e9],
      [6.121390755173323e27, 2.62794061679e11],
      [1.8364172265519969e28, -3.11129442739e11],
      [3.9488971734353397e28, -1.042720204611e12],
      [6.110398784157841e28, 1.45746287833e11],
      [6.745245411083331e28, -1.81816163647e11],
      [5.181130533150965e28, -3.454125728271e12],
      [2.6304201168304896e28, 1.426492876753e12],
      [7.93301305075862e27, -4.24963573059e11],
      [1.075971885045422e27, 1.4398231221e10],
    ],
  ] as [TwoF64[], TwoF64[]],
};
